import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { empresaAPI } from '../lib/api';

const ThemeContext = createContext(null);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Default theme palettes
const defaultLight = {
    primary: '#0891B2',
    secondary: '#164E63',
    accent: '#14B8A6',
    bg: '#F8FAFC',
    surface: '#FFFFFF',
    text: '#0C1B2A',
    text_muted: '#5B7A8A',
    border: '#CBD5E1',
    sidebar_bg: '#FFFFFF',
    sidebar_text: '#164E63',
    sidebar_active: '#0891B2',
    header_bg: '#FFFFFF',
    header_text: '#0C1B2A',
    success: '#059669',
    warning: '#D97706',
    danger: '#DC2626',
    info: '#0284C7',
};

const defaultDark = {
    primary: '#22D3EE',
    secondary: '#38BDF8',
    accent: '#14B8A6',
    bg: '#0A1929',
    surface: '#0D2137',
    text: '#E2E8F0',
    text_muted: '#8BA4B5',
    border: '#1E3A52',
    sidebar_bg: '#071422',
    sidebar_text: '#A0C4D4',
    sidebar_active: '#22D3EE',
    header_bg: '#0D2137',
    header_text: '#E2E8F0',
    success: '#34D399',
    warning: '#FBBF24',
    danger: '#F87171',
    info: '#38BDF8',
};

const defaultTema = {
    light: defaultLight,
    dark: defaultDark,
    mode_default: 'light',
    allow_user_toggle: true,
};

function hexToHSL(hex) {
    if (!hex || typeof hex !== 'string') return '0 0% 50%';
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0 0% 50%';
    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; }
    else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
            default: h = 0;
        }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function computeForeground(bgHex) {
    if (!bgHex) return '0 0% 100%';
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(bgHex);
    if (!result) return '0 0% 100%';
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.5 ? '240 10% 4%' : '0 0% 100%';
}

/**
 * Apply a full palette to CSS custom properties on :root.
 * Maps EVERY shadcn/Tailwind CSS variable from the theme palette.
 */
function applyPalette(palette) {
    const root = document.documentElement;
    if (!palette) return;

    const p = palette;

    // Set raw hex vars for direct use (--theme-primary, etc.)
    Object.entries(p).forEach(([key, value]) => {
        if (value && typeof value === 'string' && value.startsWith('#')) {
            root.style.setProperty(`--theme-${key.replace(/_/g, '-')}`, value);
        }
    });

    // Map to shadcn HSL CSS variables
    const set = (varName, hex) => {
        if (hex) root.style.setProperty(varName, hexToHSL(hex));
    };
    const setFg = (varName, bgHex) => {
        if (bgHex) root.style.setProperty(varName, computeForeground(bgHex));
    };

    // Core
    set('--primary', p.primary);
    setFg('--primary-foreground', p.primary);

    set('--background', p.bg);
    set('--foreground', p.text);

    set('--card', p.surface);
    set('--card-foreground', p.text);

    set('--popover', p.surface);
    set('--popover-foreground', p.text);

    set('--secondary', p.secondary);
    setFg('--secondary-foreground', p.secondary);

    set('--muted', p.surface);
    set('--muted-foreground', p.text_muted);

    set('--accent', p.accent);
    setFg('--accent-foreground', p.accent);

    set('--destructive', p.danger);
    setFg('--destructive-foreground', p.danger);

    set('--success', p.success);
    setFg('--success-foreground', p.success);

    set('--warning', p.warning);
    setFg('--warning-foreground', p.warning);

    set('--info', p.info);
    setFg('--info-foreground', p.info);

    set('--border', p.border);
    set('--input', p.border);
    set('--ring', p.primary);
}

export const ThemeProvider = ({ children }) => {
    const [tema, setTema] = useState(defaultTema);
    const [mode, setMode] = useState(() => {
        return localStorage.getItem('pi-theme-mode') || 'light';
    });
    const [allowUserToggle, setAllowUserToggle] = useState(true);

    // Resolve initial mode from empresa tema
    const resolveMode = useCallback((temaData) => {
        const userPref = localStorage.getItem('pi-theme-mode');
        if (userPref) return userPref;
        const modeDefault = temaData?.mode_default || 'light';
        if (modeDefault === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return modeDefault;
    }, []);

    // Load tema from empresa after login
    const loadTema = useCallback(async (empresaId) => {
        if (!empresaId) return;
        try {
            const res = await empresaAPI.getTema(empresaId);
            const temaData = res.data || {};
            const merged = {
                light: { ...defaultLight, ...(temaData.light || {}) },
                dark: { ...defaultDark, ...(temaData.dark || {}) },
                mode_default: temaData.mode_default || 'light',
                allow_user_toggle: temaData.allow_user_toggle !== false,
                brand_show_title: temaData.brand_show_title,
                brand_show_nombre: temaData.brand_show_nombre,
                brand_title_size: temaData.brand_title_size,
                brand_nombre_size: temaData.brand_nombre_size,
                brand_logo_height: temaData.brand_logo_height,
                brand_logo_top: temaData.brand_logo_top,
                brand_area_height: temaData.brand_area_height,
                brand_area_padding: temaData.brand_area_padding,
            };
            setTema(merged);
            setAllowUserToggle(merged.allow_user_toggle);
            const resolved = resolveMode(merged);
            setMode(resolved);
            // Force immediate apply
            const palette = resolved === 'dark' ? merged.dark : merged.light;
            applyPalette(palette);
        } catch (err) {
            console.error('Error loading tema:', err);
        }
    }, [resolveMode]);

    // Apply mode to DOM whenever mode or tema changes
    useEffect(() => {
        const root = document.documentElement;
        if (mode === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        localStorage.setItem('pi-theme-mode', mode);
        const palette = mode === 'dark' ? (tema.dark || defaultDark) : (tema.light || defaultLight);
        applyPalette(palette);
    }, [mode, tema]);

    // Listen for system theme changes when mode_default is "system"
    useEffect(() => {
        if (tema.mode_default !== 'system') return;
        const userPref = localStorage.getItem('pi-theme-mode');
        if (userPref) return;

        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setMode(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [tema.mode_default]);

    const toggleMode = useCallback(() => {
        setMode(prev => {
            const next = prev === 'light' ? 'dark' : 'light';
            localStorage.setItem('pi-theme-mode', next);
            return next;
        });
    }, []);

    // Update manifest link after login
    const updateManifest = useCallback((subdominio) => {
        if (!subdominio) return;
        const existing = document.querySelector('link[rel="manifest"]');
        if (existing) {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            existing.href = `${backendUrl}/api/manifest/${subdominio}.json`;
        }
    }, []);

    const value = {
        tema,
        setTema,
        mode,
        setMode,
        toggleMode,
        isDark: mode === 'dark',
        allowUserToggle,
        loadTema,
        updateManifest,
        // Legacy compat
        theme: mode,
        toggleTheme: toggleMode,
        setDarkMode: () => setMode('dark'),
        themeConfig: {
            primary_color: (mode === 'dark' ? tema.dark : tema.light)?.primary || '#0891B2',
        },
        primaryColor: (mode === 'dark' ? tema.dark : tema.light)?.primary || '#0891B2',
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
